import React from 'react';

export const HomeBanners = ({
  isAuthenticated, authLoading, isProcessingOAuth, user, credits,
  detecting, detectionBanner, outOfScopeBanner, setOutOfScopeBanner,
  warningBanner, setWarningBanner, charLimitWarning, setCharLimitWarning,
  MAX_CHAR_HOME, inputLength, navigate
}) => {
  return (
    <>
      {/* V37.44.11 — Free-access mode. Low/Zero Credit warning banner
          permanently removed from the UI. Backend credit accounting is
          unchanged; users are gated by the 10-generations / 24h cap
          (displayed via the header daily-cap badge) rather than credits. */}
      
      {/* Detecting Mode Banner */}
      {detecting && (
        <div className="mb-3 p-2 sm:p-2.5 bg-gray-100 border border-[#BF5743] rounded-lg sm:rounded-xl text-center animate-fade-in">
          <div className="flex items-center justify-center space-x-2">
            <svg className="animate-spin h-4 w-4 text-[#BF5743]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-xs sm:text-sm font-semibold text-black">Detecting mode...</span>
          </div>
        </div>
      )}
      
      {/* Detection Banner */}
      {detectionBanner && (
        <div className="mb-3 p-2 sm:p-2.5 bg-gray-100 border border-[#BF5743] rounded-lg sm:rounded-xl text-center animate-fade-in">
          <p className="text-xs sm:text-sm font-semibold text-black px-2">
            {detectionBanner.message}
          </p>
        </div>
      )}
      
      {/* Out of Scope Banner */}
      {outOfScopeBanner && (
        <div className="mb-3 p-2 sm:p-2.5 bg-gray-100 border border-[#BF5743] rounded-lg sm:rounded-xl animate-fade-in flex items-center justify-between">
          <p className="text-xs sm:text-sm font-semibold text-black flex-1 text-center px-2">
            Out of scope! Please ask an IT related question
          </p>
          <button
            onClick={() => setOutOfScopeBanner(null)}
            className="ml-2 text-gray-600 hover:text-gray-900 text-lg leading-none flex-shrink-0"
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}
      
      {/* Warning Banner */}
      {warningBanner && (
        <div className={`mb-3 p-2.5 rounded-xl animate-fade-in ${
          warningBanner.type === 'auth' 
            ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-400' 
            : warningBanner.type === 'credit'
              ? 'bg-gradient-to-r from-red-50 to-orange-50 border border-red-500'
              : 'bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-400'
        }`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center space-x-2.5">
              <span className="text-lg">
                {warningBanner.type === 'auth' ? '\u{1F512}' : warningBanner.type === 'credit' ? '\u{1F4B3}' : '\u26A0\uFE0F'}
              </span>
              <div>
                <span className={`text-sm font-semibold ${
                  warningBanner.type === 'auth' ? 'text-blue-800' : warningBanner.type === 'credit' ? 'text-red-800' : 'text-orange-800'
                }`}>
                  {warningBanner.message}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {warningBanner.actionLabel && warningBanner.onAction && (
                <button
                  onClick={warningBanner.onAction}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all whitespace-nowrap ${
                    warningBanner.type === 'credit' 
                      ? 'bg-red-600 text-white hover:bg-red-700' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {warningBanner.actionLabel}
                </button>
              )}
              <button 
                onClick={() => setWarningBanner(null)}
                className="px-2 py-1 text-gray-600 hover:text-gray-900 transition-colors text-sm"
                title="Dismiss"
              >
                &#x2715;
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Character Limit Warning */}
      {charLimitWarning && (
        <div 
          className="mb-3 p-4 rounded-xl flex items-center justify-between"
          style={{
            backgroundColor: '#B0B0B0',
            border: '1px solid #9CA3AF',
            minHeight: '60px'
          }}
        >
          <div className="flex items-center space-x-3">
            <div 
              className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: 'rgba(139, 0, 0, 0.1)',
                border: '2px solid #8B0000'
              }}
            >
              <span style={{ color: '#8B0000', fontSize: '20px', fontWeight: 'bold' }}>!</span>
            </div>
            <div>
              <p className="text-sm font-semibold" style={{ color: '#8B0000' }}>
                Character Limit Exceeded
              </p>
              <p className="text-xs mt-1" style={{ color: '#8B0000', opacity: 0.9 }}>
                Please reduce your input to {MAX_CHAR_HOME} characters or less. Current: {inputLength} characters.
              </p>
            </div>
          </div>
          <button
            onClick={() => setCharLimitWarning(false)}
            className="flex-shrink-0 ml-4 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200"
            style={{
              backgroundColor: 'rgba(139, 0, 0, 0.1)',
              border: '1px solid #8B0000'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(139, 0, 0, 0.2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(139, 0, 0, 0.1)'; }}
            title="Close warning"
          >
            <span style={{ color: '#8B0000', fontSize: '18px', fontWeight: 'bold' }}>&times;</span>
          </button>
        </div>
      )}
    </>
  );
};
